import {defineStore} from 'pinia'
import {ref} from 'vue'
import {useFormProcessStore} from './formProcess'

export const useFormConfigStore = defineStore('formConfig', () => {
    const formIdentifier = ref(null)
    const formFields = ref([])
    const formRulesInitial = ref({})
    const formRules = ref({})
    const formFieldsRequired = ref({})
    const formFieldsWithWildcardInRule = ref([])
    const formHelpTexts = ref({})
    const formFieldsWithWildcardInHelpText = ref([])
    const formFieldsLabel = ref({})
    const formFieldsWithWildcardInLabel = ref([])

    function storeFormConfigData() {
        formIdentifier.value = useFormProcessStore().formProcessData.form.identifier
        formFields.value = useFormProcessStore().formProcessData.form.formFields
        formRulesInitial.value = useFormProcessStore().formProcessData.rules

        getFormRules()
        getRequiredFields()
        getHelpTexts()
        getLabels()
    }

    function getFormRules() {
        Object.keys(formRulesInitial.value).forEach((fieldIdentifier) => {
            const rules = handleRules(fieldIdentifier)

            formRules.value[fieldIdentifier] = rules.join('|')
        })
    }

    function getRequiredFields() {
        Object.keys(formRulesInitial.value).forEach((fieldIdentifier) => {
            formFieldsRequired.value[fieldIdentifier] = formRulesInitial.value[fieldIdentifier].split('|').includes('required')
        })
    }

    function getHelpTexts() {
        formFields.value.forEach(field => {
            formHelpTexts.value[field.fieldIdentifier] = handleHelpTexts(field.helpText ?? null, field)
        })
    }

    function getLabels() {
        formFields.value.forEach(field => {
            formFieldsLabel.value[field.fieldIdentifier] = handleLabels(field.label ?? null, field)
        })
    }

    function updateFormRules() {
        getFormRules()
    }

    function updateHelpTexts() {
        getHelpTexts()
    }

    function handleRules(fieldIdentifier) {
        let rules = formRulesInitial.value[fieldIdentifier].split('|')

        const isNumberField = formRulesInitial.value[fieldIdentifier].includes('numeric') ||
            formRulesInitial.value[fieldIdentifier].includes('integer') ||
            formRulesInitial.value[fieldIdentifier].includes('decimal')

        rules.forEach((rule, index) => {
            rule = replaceFieldIdentifiersInString(rule, isNumberField)

            rule = calculateRule(rule, rules)

            rules[index] = rule
        })

        rules = handleDuplicatedRules(rules)

        return rules
    }

    function replaceFieldIdentifiersInString(string, isNumberField) {
        const formProcessData = useFormProcessStore().formProcessData

        while (string && string.includes('{')) {
            const replaceFieldIdentifier = string.substring(string.indexOf('{') + 1, string.indexOf('}'))

            if (!formFieldsWithWildcardInRule.value.includes(replaceFieldIdentifier))
                formFieldsWithWildcardInRule.value.push(replaceFieldIdentifier)

            if (replaceFieldIdentifier in formProcessData.data && formProcessData.data[replaceFieldIdentifier] !== null) {
                let replaceValue = formProcessData.data[replaceFieldIdentifier] || 0

                if (isNumberField) replaceValue = replaceValue.toString().replace(',', '.')

                string = string.replace('{' + replaceFieldIdentifier + '}', replaceValue)
            } else if (replaceFieldIdentifier.startsWith('yearData')) {
                const year_id = formProcessData.year_id ?? null
                let yearData = formProcessData.process?.fundingProgram?.yearData?.find(yearData => yearData.year_id === year_id) ?? 0

                let replaceValue = 0
                if (yearData !== 0) {
                    const fieldKey = replaceFieldIdentifier.replace('yearData.', '')
                    const fieldKeyValues = fieldKey.split('.')
                    replaceValue = yearData
                    fieldKeyValues.forEach((key) => {
                        if (replaceValue == null || !(key in replaceValue)) {
                            replaceValue = ''
                        } else {
                            replaceValue = replaceValue[key]
                        }
                    })
                }

                string = string.replace('{' + replaceFieldIdentifier + '}', replaceValue)
            } else if (replaceFieldIdentifier.includes('.')) {
                let replaceValue = []

                replaceValue = formProcessData

                replaceFieldIdentifier.split('.').forEach((key) => {
                    if (replaceValue == null || !(key in replaceValue)) {
                        replaceValue = null
                    } else {
                        replaceValue = replaceValue[key]
                    }
                })

                if (!replaceValue) replaceValue = 0

                if (isNumberField) replaceValue = replaceValue.toString().replace(',', '.')

                string = string.replace('{' + replaceFieldIdentifier + '}', replaceValue)
            } else if (replaceFieldIdentifier in formProcessData) {
                let replaceValue = formProcessData[replaceFieldIdentifier] || 0

                if (isNumberField) replaceValue = replaceValue.toString().replace(',', '.')

                string = string.replace('{' + replaceFieldIdentifier + '}', replaceValue)
            } else {
                string = null
            }
        }

        return string
    }

    function calculateRule(rule, otherFieldRules) {
        let calc = null
        let colonIndex = null
        let ruleType = null
        let isDateValidation = false

        if (rule == null) return rule

        // Check if the rule is a date validation rule
        if (rule.startsWith('afterOrEqualDate') || rule.startsWith('beforeOrEqualDate')) {
            isDateValidation = true
        }

        // Check if the rule contains arithmetic operations and get the rules values
        if (!isDateValidation && (rule.includes('+') || rule.includes('-') || rule.includes('*') || rule.includes('/'))) {
            colonIndex = rule.indexOf(':')
            if (colonIndex !== -1) {
                calc = rule.substring(colonIndex + 1).trim()
            }

            ruleType = rule.substring(0, colonIndex)
        }

        if (calc) {
            // Split the calc part into individual components, if necessary
            let parts = calc.split(',')

            for (let i = 0; i < parts.length; i++) {
                if (parts[i] === undefined) continue

                let part = parts[i].trim()

                if (part.startsWith('Math.max(') || part.startsWith('Math.min(')) {
                    part = parts[i] + ',' + parts[i + 1]
                    delete parts[i + 1]
                }

                // Check if this part contains an arithmetic expression
                if (part.match(/[+\-*/]/)) {
                    try {
                        const result = eval(part)

                        let roundDigits = null
                        let roundType = null

                        otherFieldRules.forEach((otherFieldRule) => {
                            if (otherFieldRule === 'currency') {
                                roundDigits = 2
                                if (ruleType === 'min_value') {
                                    roundType = 'ceil'
                                } else if (ruleType === 'max_value') {
                                    roundType = 'floor'
                                } else {
                                    roundType = 'round'
                                }
                            } else if (otherFieldRule.startsWith('decimal:')) {
                                roundDigits = Number(otherFieldRule.split(':')[1])
                                roundType = 'round'
                            } else if (otherFieldRule === 'integer') {
                                roundDigits = 0
                                roundType = 'round'
                            }
                        })

                        switch (roundType) {
                            case 'ceil':
                                parts[i] = Math.ceil(Number(result) * Math.pow(10, roundDigits)) / Math.pow(10, roundDigits)
                                break
                            case 'floor':
                                parts[i] = Math.floor(Number(result) * Math.pow(10, roundDigits)) / Math.pow(10, roundDigits)
                                break
                            case 'round':
                                parts[i] = Math.round(Number(result) * Math.pow(10, roundDigits)) / Math.pow(10, roundDigits)
                                break
                            default:
                                parts[i] = Number(result)
                                break
                        }
                    } catch (e) {
                        console.error("Error evaluating calc:", e)
                    }
                }
            }

            parts = parts.filter(part => part !== undefined && part !== null && part !== '')

            // Rejoin the parts into the final calculated rule
            rule = rule.substring(0, colonIndex + 1) + parts.join(',')
        }

        return rule
    }

    function handleDuplicatedRules(rules) {
        const rulesKeyedByRuleName = {}
        rules.forEach(rule => {
            if (rule == null) return
            const ruleName = (rule.indexOf(':') >= 0) ? rule.substring(0, rule.indexOf(':')) : rule
            if (!(ruleName in rulesKeyedByRuleName)) rulesKeyedByRuleName[ruleName] = []
            rulesKeyedByRuleName[ruleName].push(rule.replace(ruleName + ':', ''))
        })

        Object.keys(rulesKeyedByRuleName).forEach(ruleName => {
            if (rulesKeyedByRuleName[ruleName].length > 1) {
                let value = null

                switch (ruleName) {
                    case 'max_value':
                    case 'max':
                        rulesKeyedByRuleName[ruleName].forEach((rule, index) => {
                            rulesKeyedByRuleName[ruleName][index] = parseFloat(rule)
                        })

                        value = Math.min(...rulesKeyedByRuleName[ruleName])
                        break
                    case 'min_value':
                    case 'min':
                        rulesKeyedByRuleName[ruleName].forEach((rule, index) => {
                            rulesKeyedByRuleName[ruleName][index] = parseFloat(rule)
                        })

                        value = Math.max(...rulesKeyedByRuleName[ruleName])
                        break
                    case 'max_if_greater_zero':
                        rulesKeyedByRuleName[ruleName].forEach((rule, index) => {
                            if (rule > 0) {
                                rulesKeyedByRuleName[ruleName][index] = parseFloat(rule)
                            } else {
                                rulesKeyedByRuleName[ruleName].splice(index, 1)
                            }
                        })

                        value = Math.min(...rulesKeyedByRuleName[ruleName])
                        break
                    default:
                        value = rulesKeyedByRuleName[ruleName][0]
                }

                rulesKeyedByRuleName[ruleName] = [value]
            }
        })

        rules = []

        Object.keys(rulesKeyedByRuleName).forEach(ruleName => {
            if (ruleName === rulesKeyedByRuleName[ruleName][0]) {
                rules.push(ruleName)
            } else {
                rules.push(ruleName + ':' + rulesKeyedByRuleName[ruleName][0])
            }
        })

        return rules
    }

    function handleHelpTexts(helpText, field) {
        const formProcessData = useFormProcessStore().formProcessData

        const isNumberField = formRulesInitial.value[field.fieldIdentifier].includes('numeric') ||
            formRulesInitial.value[field.fieldIdentifier].includes('integer') ||
            formRulesInitial.value[field.fieldIdentifier].includes('decimal') ||
            formRulesInitial.value[field.fieldIdentifier].includes('currency')

        while (helpText && helpText.includes('{')) {
            const replaceFieldIdentifier = helpText.substring(helpText.indexOf('{') + 1, helpText.indexOf('}'))

            if (!formFieldsWithWildcardInHelpText.value.includes(replaceFieldIdentifier))
                formFieldsWithWildcardInHelpText.value.push(replaceFieldIdentifier)

            if (formProcessData.data[replaceFieldIdentifier] !== undefined && formProcessData.data[replaceFieldIdentifier] !== null) {
                let replaceValue = ''

                if (isNumberField && (formProcessData.data[replaceFieldIdentifier] === '' || isNaN(formProcessData.data[replaceFieldIdentifier]))) {
                    replaceValue = 0
                } else {
                    replaceValue = formProcessData.data[replaceFieldIdentifier]
                }

                if (isNumberField) replaceValue = replaceValue.toString().replace(',', '.')

                helpText = helpText.replace('{' + replaceFieldIdentifier + '}', replaceValue)
            } else if (replaceFieldIdentifier.startsWith('yearData')) {
                const year_id = formProcessData.year_id ?? null
                let yearData = formProcessData.process?.fundingProgram?.yearData?.find(yearData => yearData.year_id === year_id) ?? 0

                let replaceValue = 0
                if (yearData !== 0) {
                    replaceValue = yearData[replaceFieldIdentifier.replace('yearData.', '')] ?? ''
                    replaceValue = replaceValue.toString()?.replace('.', ',')
                }

                helpText = helpText.replace('{' + replaceFieldIdentifier + '}', replaceValue)
            } else if (replaceFieldIdentifier.includes('.')) {
                let replaceValue = []

                if (replaceFieldIdentifier.startsWith('yearData')) {
                    const year_id = formProcessData.year_id ?? null
                    let yearData = formProcessData.process?.fundingProgram?.yearData?.find(yearData => yearData.year_id === year_id) ?? null

                    replaceValue['yearData'] = yearData ?? null
                } else {
                    replaceValue = formProcessData
                }

                replaceFieldIdentifier.split('.').forEach((key) => {
                    if (replaceValue == null || !(key in replaceValue)) {
                        replaceValue = null
                    } else {
                        replaceValue = replaceValue[key]
                    }
                })

                if (!replaceValue) replaceValue = 0

                if (isNumberField) replaceValue = replaceValue.toString().replace(',', '.')

                helpText = helpText.replace('{' + replaceFieldIdentifier + '}', replaceValue)
            } else {
                helpText = null
            }
        }

        if (helpText) {
            while (helpText.includes('calc(')) {
                let calcText = helpText.substring(helpText.indexOf('calc(') + 5, helpText.indexOf(')endcalc'))
                try {
                    const result = eval(calcText)

                    if (field && field.rulesVue.includes('currency')) {
                        helpText = helpText.replace('calc(' + calcText + ')endcalc', Number(result).toFixed(2).replace('.', ','))
                    } else {
                        helpText = helpText.replace('calc(' + calcText + ')endcalc', Number(result))
                    }

                } catch (e) {
                    console.error("Error evaluating help text:", e)
                }
            }
        }

        return helpText
    }

    function handleLabels(label, field) {
        const formProcessData = useFormProcessStore().formProcessData

        const isNumberField = formRulesInitial.value[field.fieldIdentifier].includes('numeric') ||
            formRulesInitial.value[field.fieldIdentifier].includes('integer') ||
            formRulesInitial.value[field.fieldIdentifier].includes('decimal') ||
            formRulesInitial.value[field.fieldIdentifier].includes('currency')

        while (label && label.includes('{')) {
            const replaceFieldIdentifier = label.substring(label.indexOf('{') + 1, label.indexOf('}'))

            if (!formFieldsWithWildcardInLabel.value.includes(replaceFieldIdentifier))
                formFieldsWithWildcardInLabel.value.push(replaceFieldIdentifier)

            if (formProcessData.data[replaceFieldIdentifier] !== undefined && formProcessData.data[replaceFieldIdentifier] !== null) {
                let replaceValue = formProcessData.data[replaceFieldIdentifier]

                if (isNumberField) replaceValue = replaceValue.toString().replace(',', '.')

                label = label.replace('{' + replaceFieldIdentifier + '}', replaceValue)
            } else if (replaceFieldIdentifier.startsWith('yearData')) {
                const year_id = formProcessData.year_id ?? null
                let yearData = formProcessData.process?.fundingProgram?.yearData?.find(yearData => yearData.year_id === year_id) ?? 0

                let replaceValue = 0
                if (yearData !== 0) {
                    replaceValue = yearData[replaceFieldIdentifier.replace('yearData.', '')] ?? ''
                    replaceValue = replaceValue.toString()?.replace('.', ',')
                }

                label = label.replace('{' + replaceFieldIdentifier + '}', replaceValue)
            } else if (replaceFieldIdentifier.includes('.')) {
                let replaceValue = []

                if (replaceFieldIdentifier.startsWith('yearData')) {
                    const year_id = formProcessData.year_id ?? null
                    let yearData = formProcessData.process?.fundingProgram?.yearData?.find(yearData => yearData.year_id === year_id) ?? null

                    replaceValue['yearData'] = yearData ?? null
                } else {
                    replaceValue = formProcessData
                }

                replaceFieldIdentifier.split('.').forEach((key) => {
                    if (replaceValue == null || !(key in replaceValue)) {
                        replaceValue = null
                    } else {
                        replaceValue = replaceValue[key]
                    }
                })

                if (!replaceValue) replaceValue = 0

                if (isNumberField) replaceValue = replaceValue.toString().replace(',', '.')

                label = label.replace('{' + replaceFieldIdentifier + '}', replaceValue)
            } else {
                label = null
            }
        }

        if (label && label.includes('calc(')) {
            let calcText = label.substring(label.indexOf('calc(') + 5, label.indexOf(')endcalc'))
            try {
                const result = eval(calcText)

                if (field && field.rulesVue.includes('currency')) {
                    label = label.replace('calc(' + calcText + ')endcalc', Number(result).toFixed(2).replace('.', ','))
                } else {
                    label = label.replace('calc(' + calcText + ')endcalc', Number(result))
                }

            } catch (e) {
                console.error("Error evaluating calc:", e)
            }
        }

        return label
    }

    function resetData() {
        formIdentifier.value = null
        formFields.value = []
        formRulesInitial.value = {}
        formRules.value = {}
        formFieldsRequired.value = {}
        formFieldsWithWildcardInRule.value = []
        formHelpTexts.value = {}
        formFieldsWithWildcardInHelpText.value = []
        formFieldsLabel.value = {}
        formFieldsWithWildcardInLabel.value = []
    }

    return {
        formIdentifier,
        formFields,
        formRulesInitial,
        formRules,
        formFieldsRequired,
        formFieldsWithWildcardInRule,
        formHelpTexts,
        formFieldsWithWildcardInHelpText,
        formFieldsLabel,
        formFieldsWithWildcardInLabel,
        storeFormConfigData,
        updateFormRules,
        updateHelpTexts,
        replaceFieldIdentifiersInString,
        resetData
    }
})
